import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, Home, BookOpen, Images, Trophy, Users, Navigation } from "lucide-react";
import { toast } from "sonner";
import { BRAND, CAMPUSES } from "@/data/content";
import { openWhatsApp, DEMO_MSG } from "@/lib/site";
import { useSettings } from "@/hooks/useSettings";
import { Logo } from "@/components/Logo";
import { InstagramIcon, YoutubeIcon, LinkedinIcon, FacebookIcon } from "@/components/BrandIcons";

const QUICK_LINKS = [
  { to: "/", label: "Home", icon: Home, id: "home" },
  { to: "/content", label: "Content", icon: BookOpen, id: "content" },
  { to: "/gallery", label: "Gallery", icon: Images, id: "gallery" },
  { to: "/results", label: "Results and Achievements", icon: Trophy, id: "results" },
  { to: "/about", label: "About Us", icon: Users, id: "about" },
  { to: "/visit", label: "Visit Us", icon: Navigation, id: "visit" },
];

const socialToast = () => toast.info("Link coming soon — this page hasn't been added yet.");

export const SocialIcons = () => {
  const { facebook, linkedin } = useSettings();
  const cls = "flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:scale-110 hover:shadow-lg";
  const ext = (href, testid, label, Icon) =>
    href ? (
      <a data-testid={testid} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className={cls}><Icon className="h-5 w-5" /></a>
    ) : (
      <button data-testid={testid} onClick={socialToast} aria-label={label} className={cls}><Icon className="h-5 w-5" /></button>
    );
  return (
    <div className="flex items-center gap-3">
      <a data-testid="social-instagram" href={BRAND.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className={cls}>
        <InstagramIcon className="h-5 w-5" />
      </a>
      <a data-testid="social-youtube" href={BRAND.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className={cls}>
        <YoutubeIcon className="h-5 w-5" />
      </a>
      {ext(facebook, "social-facebook", "Facebook", FacebookIcon)}
      {ext(linkedin, "social-linkedin", "LinkedIn", LinkedinIcon)}
    </div>
  );
};

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-forest pb-24 text-cream lg:pb-0">
    <div className="border-b border-terra/15 bg-[#F3F5F7]">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-5 sm:flex-row sm:px-8">
        <p data-testid="footer-admissions-banner" className="text-center text-sm font-semibold tracking-wide text-terra">
          Admissions Open for Session 2026–27 · Starts 15th March 2026 · Limited Seats — Hurry Up!
        </p>
        <button
          data-testid="footer-demo-btn"
          onClick={() => openWhatsApp(DEMO_MSG)}
          className="flex items-center gap-2 rounded-full bg-terra px-5 py-2 text-xs font-bold text-forest transition-transform duration-300 hover:scale-105"
        >
          <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.2} /> Book Demo
        </button>
      </div>
    </div>

    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <div className="flex items-center gap-3">
          <Logo size="h-9 w-9 text-base" />
          <p className="font-display text-xl font-semibold">Goyal Academy</p>
        </div>
        <p className="mt-5 font-display text-sm italic leading-relaxed text-cream/60">"{BRAND.tagline}"</p>
        <div className="mt-6"><SocialIcons /></div>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-terra">Our Campuses</p>
        <ul className="mt-5 space-y-4">
          {CAMPUSES.map((c) => (
            <li key={c.id} data-testid={`footer-${c.id}`} className="flex gap-2.5">
              <MapPin data-testid={`footer-${c.id}-icon`} className="mt-0.5 h-4 w-4 shrink-0 text-terra" strokeWidth={1.8} />
              <div>
                <p className="text-sm font-semibold text-cream/90">{c.name}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-cream/50">{c.address}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-terra">Quick Links</p>
        <ul className="mt-5 space-y-3 text-sm text-cream/70">
          {QUICK_LINKS.map((l) => (
            <li key={l.id}>
              <Link data-testid={`footer-link-${l.id}`} to={l.to} className="flex items-center gap-2.5 transition-colors hover:text-terra">
                <l.icon data-testid={`footer-link-${l.id}-icon`} className="h-4 w-4 shrink-0 text-terra" strokeWidth={1.8} /> {l.label}
              </Link>
            </li>
          ))}
          <li><a data-testid="footer-link-instagram" href={BRAND.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-terra"><InstagramIcon data-testid="footer-link-instagram-icon" className="h-4 w-4 shrink-0" /> Instagram {BRAND.instagramHandle}</a></li>
          <li><a data-testid="footer-link-youtube" href={BRAND.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-terra"><YoutubeIcon data-testid="footer-link-youtube-icon" className="h-4 w-4 shrink-0" /> YouTube — Goyal Academy</a></li>
        </ul>
      </div>

      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-terra">Talk To Us</p>
        <ul className="mt-5 space-y-3">
          {BRAND.phones.slice(0, 2).map((p) => (
            <li key={p}>
              <a data-testid={`footer-phone-${p}`} href={`tel:+91${p}`} className="flex items-center gap-2.5 text-sm text-cream/80 transition-colors hover:text-terra">
                <Phone className="h-4 w-4 text-terra" strokeWidth={1.8} /> +91 {p.slice(0, 5)} {p.slice(5)}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-5 text-xs leading-relaxed text-cream/40">English Grooming Classes: +91 94245 62299</p>
        <p className="mt-2 text-xs text-cream/40">{BRAND.website}</p>
      </div>
    </div>

    <div className="border-t border-cream/10">
      <p className="mx-auto max-w-7xl px-5 py-6 text-center text-xs text-cream/40 sm:px-8">
        © Goyal Academy — An Investment In Knowledge Pays The Best Interest.
      </p>
    </div>
  </footer>
);
